import React, { useState, useEffect, useCallback, useRef } from 'react'
import { format, parseISO, differenceInYears } from 'date-fns'
import { es } from 'date-fns/locale'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  Upload, FileCheck, FileX, Trash2, Eye, CheckCircle2, XCircle,
  Shield, ShieldCheck, Phone, Mail, Calendar, Hash, User,
  Fingerprint, Users2, BookOpen, Shirt, FileSignature, ArrowLeft, Camera,
} from 'lucide-react'
import {
  Button, Badge, Card, CardContent, Label, Input,
} from '../ui'
import { FeaturedLayout } from '../layouts'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs'
import PlayerAvatar from '../ui/PlayerAvatar'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { usePhotoUpload } from '../../hooks/usePhotoUpload'
import { useDeportes } from '../../hooks/useDeportes'
import {
  getPlayerById,
  getPlayerDocuments,
  uploadPlayerDocument,
  deletePlayerDocument,
  verifyPlayerDocument,
  getDocumentUrl,
  BASE_DOCUMENT_TYPES,
  updatePlayerGroupOverrides,
  getPrendasDeJugador,
  getConsentimientoVigente,
  consentimientoVerificado,
  puedeUsarImagen,
  revocarUsoImagen,
} from '../../lib/supabase'
import { useOrganization } from '../../contexts/OrganizationContext'
import { useConfirmDialog } from '../../hooks/useConfirmDialog'
import SolicitarConsentimientoDialog from '../consent/SolicitarConsentimientoDialog'
import ConsentimientoFisicoDialog from '../consent/ConsentimientoFisicoDialog'
import { destinoConsentimiento } from '../../lib/consent'
import { estadoDePago } from '../../lib/uniformesEstadoPago'

const statusColors = {
  activo: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pendiente: 'bg-amber-50 text-amber-700 border-amber-200',
  inactivo: 'bg-slate-50 text-slate-600 border-slate-200',
  suspendido: 'bg-rose-50 text-rose-700 border-rose-200',
  baja: 'bg-rose-50 text-rose-700 border-rose-200',
}

const calculateAge = (dateStr) => {
  if (!dateStr) return null
  try {
    return differenceInYears(new Date(), parseISO(dateStr))
  } catch {
    return null
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return '—'
  try {
    return format(parseISO(dateStr), 'dd/MM/yyyy', { locale: es })
  } catch {
    return dateStr
  }
}

// `faltante`: texto a mostrar cuando el dato esta vacio. Sin el, la fila desaparece
// y el encabezado de su bloque queda huerfano -- tres titulos apilados sin nada
// debajo. Para los datos que el sistema EXIGE (el contacto del tutor de un menor),
// ver "Sin capturar" es la informacion util; ocultarlo es esconder el problema.
const InfoRow = ({ icon: Icon, label, value, faltante = null }) => {
  const vacio = !value && value !== 0
  if (vacio && !faltante) return null
  return (
    <div className="flex items-start gap-3 py-2">
      {Icon && <Icon className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        {vacio
          ? <p className="text-sm italic text-muted-foreground">{faltante}</p>
          : <p className="text-sm font-medium break-words">{value}</p>}
      </div>
    </div>
  )
}

// =====================================================
// TAB: Informacion
// =====================================================
// Estado del consentimiento y sus acciones. Vive aqui, en la ficha, porque es donde
// el admin sabe DE QUIEN esta hablando: el tablero sirve para el envio masivo, pero
// registrar una firma en papel o revocar el uso de imagen son decisiones de un
// jugador concreto.
const ConsentimientoBloque = ({ player, onSolicitar }) => {
  const [consent, setConsent] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [fisicoOpen, setFisicoOpen] = useState(false)
  const { confirm, alert, prompt, ConfirmDialog } = useConfirmDialog()

  const cargar = useCallback(async () => {
    setCargando(true)
    const { data } = await getConsentimientoVigente(player.id)
    setConsent(data)
    setCargando(false)
  }, [player.id])

  useEffect(() => { cargar() }, [cargar])

  const verificado = consentimientoVerificado(consent)
  const imagenVigente = puedeUsarImagen(consent)

  const handleRevocar = async () => {
    const ok = await confirm(
      'Revocar uso de imagen',
      `Se registrará que ${player.nombre_completo} ya no autoriza el uso de su imagen para difusión. ` +
      'La autorización original queda en el expediente como constancia de lo que se firmó.',
      { destructive: true }
    )
    if (!ok) return
    const nota = await prompt('Revocar uso de imagen', 'Motivo o quién lo solicitó (opcional):')
    const { error } = await revocarUsoImagen(player.id, nota || null)
    if (error) { await alert('Error', error.message); return }
    cargar()
  }

  if (cargando) {
    return <p className="text-sm text-muted-foreground py-2">Cargando…</p>
  }

  return (
    <div className="space-y-2">
      {consent ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={verificado ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
              {verificado ? 'Firmado y verificado' : 'Firmado sin verificar'}
            </Badge>
            <span className="text-xs text-muted-foreground">{formatDate(consent.fecha_hora)}</span>
          </div>

          {!verificado && (
            <p className="rounded-md bg-orange-50 px-3 py-2 text-xs text-orange-900">
              Se recabó con el flujo anterior, en el que bastaba abrir un enlace. No cuenta
              como consentimiento válido: vuelve a solicitarlo.
            </p>
          )}

          <InfoRow icon={User} label="Firmó" value={
            consent.consiente_nombre
              ? `${consent.consiente_nombre}${consent.consiente_parentesco ? ` (${consent.consiente_parentesco})` : ''}`
              : null
          } faltante="No se registró" />
          <InfoRow icon={Mail} label="Contacto verificado" value={consent.consiente_contacto} faltante="No se registró" />
          <InfoRow
            icon={Shield}
            label="Uso de imagen"
            value={
              imagenVigente ? 'Autorizado'
                : consent.imagen_revocada_at ? `Revocado el ${formatDate(consent.imagen_revocada_at)}`
                : 'No autorizado'
            }
          />
        </>
      ) : (
        <p className="text-sm text-muted-foreground">Sin consentimiento registrado.</p>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onSolicitar}>
          <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
          {consent ? 'Volver a solicitar' : 'Solicitar consentimiento'}
        </Button>
        <Button variant="outline" size="sm" onClick={() => setFisicoOpen(true)}>
          <FileSignature className="h-3.5 w-3.5 mr-1.5" />
          Firma en papel
        </Button>
        {imagenVigente && (
          <Button
            variant="outline"
            size="sm"
            className="text-destructive border-destructive/30"
            onClick={handleRevocar}
          >
            Revocar imagen
          </Button>
        )}
      </div>

      <ConsentimientoFisicoDialog
        open={fisicoOpen}
        onOpenChange={setFisicoOpen}
        jugadorId={player.id}
        jugadorNombre={player.nombre_completo}
        onDone={cargar}
      />
      <ConfirmDialog />
    </div>
  )
}

const InfoTab = ({ player }) => {
  const [solicitarOpen, setSolicitarOpen] = useState(false)

  const age = calculateAge(player.fecha_nacimiento)
  const esMenor = age != null && age < 18

  // Quien paga sale de la FAMILIA; quien firma, del vinculo con el JUGADOR.
  const respPago = player.familias?.responsable?.nombre_completo || player.contacto || ''
  const vinculoTutor = (player.jugador_personas || [])[0] || null
  const tutorLegal = vinculoTutor?.personas
    ? {
        nombre: vinculoTutor.personas.nombre_completo || '',
        parentesco: vinculoTutor.parentesco || '',
        telefono: vinculoTutor.personas.telefono || '',
      }
    : null
  // Sin acentos ni mayusculas: 46% de las personas estan guardadas en MAYUSCULAS, y
  // comparar literal diria "son distintos" sobre la misma persona.
  const norm = (s) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim()
  const mismoQuePaga = !!tutorLegal && !!respPago && norm(tutorLegal.nombre) === norm(respPago)

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <CardContent className="pt-4 space-y-1">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Datos Personales</h4>
          <InfoRow icon={User} label="Nombre" value={player.nombre_completo} />
          {player.nickname && <InfoRow icon={BookOpen} label="Nickname" value={`"${player.nickname}"`} />}
          <InfoRow icon={Hash} label="Numero (club)" value={player.numero ? `#${player.numero}` : null} />
          <InfoRow icon={Calendar} label="Fecha de nacimiento" value={age != null ? `${formatDate(player.fecha_nacimiento)} (${age} anios)` : null} />
          <InfoRow icon={Users2} label="Genero" value={player.genero} />
          <InfoRow icon={Fingerprint} label="CURP" value={player.curp} />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4 space-y-1">
          {/* QUIEN RESPONDE SE LEE DE SU FAMILIA, no de las columnas de la ficha.
              `jugadores.contacto` es una copia que nadie actualiza cuando el responsable
              se captura desde Fichas por completar -- ahi el vinculo se crea contra la
              FAMILIA. Medido en PROD: 3 fichas decian "Sin capturar" teniendo responsable,
              y 18 mostraban un nombre distinto al de su familia. La familia es el espejo
              de `personas`, o sea la version viva; la ficha es el respaldo mientras esas
              columnas existan (Fase 3). */}
          {/* DOS PAPELES, NO UNO. Quien PAGA y quien FIRMA suelen ser la misma persona,
              pero no tienen por que serlo, y mezclarlos fue el error que costo varias
              vueltas: el responsable de pago cuelga de la FAMILIA (el dinero es del
              hogar) y el tutor legal cuelga del JUGADOR (la representacion es sobre un
              menor). Cuando coinciden se dice, en vez de repetir el nombre dos veces. */}
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Responsable de pago</h4>
          <InfoRow icon={User} label="Responsable" value={respPago} faltante="Sin capturar" />
          <InfoRow icon={Phone} label="Telefono" value={player.familias?.responsable?.telefono || player.telefono_contacto} faltante="Sin capturar" />
          <InfoRow icon={Mail} label="Correo" value={player.familias?.responsable?.email || player.email_tutor} faltante={null} />
          <InfoRow icon={Phone} label="Telefono jugador (uso interno)" value={player.telefono_jugador} />

          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 mt-4">Tutor legal</h4>
          {tutorLegal ? (
            mismoQuePaga ? (
              <p className="text-sm text-muted-foreground">
                <span className="text-foreground font-medium">{tutorLegal.nombre}</span>
                {tutorLegal.parentesco ? ` (${tutorLegal.parentesco})` : ''} — el mismo que paga.
              </p>
            ) : (
              <>
                <InfoRow icon={User} label="Firma por el jugador" value={tutorLegal.nombre} />
                {tutorLegal.parentesco && <InfoRow icon={Users2} label="Parentesco" value={tutorLegal.parentesco} />}
                {tutorLegal.telefono && <InfoRow icon={Phone} label="Telefono" value={tutorLegal.telefono} />}
              </>
            )
          ) : (
            <p className="text-sm text-muted-foreground">
              Sin registrar. Queda anotado cuando alguien firma el consentimiento.
            </p>
          )}

          {esMenor && !player.telefono_contacto && !player.email_tutor && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Sin teléfono ni correo del tutor no hay a dónde mandarle el consentimiento.
              Captúralos desde <strong>Editar</strong>.
            </p>
          )}

          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 mt-4">Consentimiento</h4>
          <ConsentimientoBloque player={player} onSolicitar={() => setSolicitarOpen(true)} />
        </CardContent>
      </Card>

      {/* A quien se manda: el mayor de edad firma por si mismo, el menor por su tutor
          ligado; la familia (quien PAGA) es solo el respaldo. */}
      <SolicitarConsentimientoDialog
        open={solicitarOpen}
        onOpenChange={setSolicitarOpen}
        jugadorId={player.id}
        familiaId={player.familia_id || null}
        tutorTelefono={destinoConsentimiento({
          fechaNacimiento: player.fecha_nacimiento,
          bajoTutela: player.bajo_tutela_legal,
          telefonoJugador: player.telefono_jugador,
          telefonoTutorLegal: (player.jugador_personas || []).map((v) => v?.personas?.telefono).find(Boolean),
          telefonoFamilia: player.familias?.responsable?.telefono,
          telefonoContacto: player.telefono_contacto,
        }).telefono}
        tituloDestino={player.nombre_completo}
      />
    </div>
  )
}

// =====================================================
// TAB: Grupos
// =====================================================
const GruposTab = ({ player, onRefresh }) => {
  const { label: deporteLabel } = useDeportes()
  const { alert } = useConfirmDialog()
  const [editingId, setEditingId] = useState(null)
  const [overrides, setOverrides] = useState({})
  const [saving, setSaving] = useState(false)

  const activeGroups = (player.jugadores_grupos || []).filter(
    (jg) => jg.activo && !jg.deleted && jg.grupos
  )

  const startEdit = (jg) => {
    setEditingId(jg.id)
    setOverrides({
      numero_jersey: jg.numero_jersey || '',
      nickname: jg.nickname || '',
      posicion: jg.posicion || '',
      notas: jg.notas || '',
    })
  }

  const saveEdit = async () => {
    setSaving(true)
    const { error } = await updatePlayerGroupOverrides(editingId, overrides)
    setSaving(false)
    if (error) { await alert('Error', error.message || 'No se pudo guardar'); return }
    setEditingId(null)
    onRefresh?.()
  }

  if (activeGroups.length === 0) {
    return <p className="text-muted-foreground text-sm py-4">Este jugador no esta asignado a ningun grupo.</p>
  }

  return (
    <div className="space-y-3">
      {activeGroups.map((jg) => (
        <Card key={jg.id}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{jg.grupos.nombre_del_grupo}</span>
                {jg.grupos.deporte && (
                  <Badge variant="outline" className="text-xs">{deporteLabel(jg.grupos.deporte)}</Badge>
                )}
              </div>
              {editingId !== jg.id && (
                <Button variant="ghost" size="sm" onClick={() => startEdit(jg)}>
                  Editar
                </Button>
              )}
            </div>

            {editingId === jg.id ? (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-1">
                  <Label className="text-xs">Jersey en grupo</Label>
                  <Input
                    type="number" min="0" max="999"
                    value={overrides.numero_jersey}
                    onChange={(e) => setOverrides((p) => ({ ...p, numero_jersey: e.target.value ? parseInt(e.target.value) : '' }))}
                    placeholder={player.numero ? `Hereda #${player.numero}` : '#'}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Nickname en grupo</Label>
                  <Input
                    value={overrides.nickname}
                    onChange={(e) => setOverrides((p) => ({ ...p, nickname: e.target.value }))}
                    placeholder={player.nickname ? `Hereda "${player.nickname}"` : 'Nickname'}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Posicion</Label>
                  <Input
                    value={overrides.posicion}
                    onChange={(e) => setOverrides((p) => ({ ...p, posicion: e.target.value }))}
                    placeholder="Ej: Quarterback"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Notas</Label>
                  <Input
                    value={overrides.notas}
                    onChange={(e) => setOverrides((p) => ({ ...p, notas: e.target.value }))}
                    placeholder="Notas del coach"
                  />
                </div>
                <div className="col-span-2 flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setEditingId(null)} disabled={saving}>Cancelar</Button>
                  <Button size="sm" onClick={saveEdit} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm text-muted-foreground">
                <span>Jersey: <strong className="text-foreground">{jg.numero_jersey ?? (player.numero ? `#${player.numero} (club)` : '—')}</strong></span>
                <span>Nickname: <strong className="text-foreground">{jg.nickname || player.nickname || '—'}</strong></span>
                <span>Posicion: <strong className="text-foreground">{jg.posicion || '—'}</strong></span>
                <span>Estatus: <strong className="text-foreground">{jg.estatus || '—'}</strong></span>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

// =====================================================
// TAB: Documentos
// =====================================================
const DocumentosTab = ({ player }) => {
  const { confirm, alert, ConfirmDialog: DocConfirmDialog } = useConfirmDialog()
  const { organization } = useOrganization()
  const orgId = organization?.id
  const [documents, setDocuments] = useState([])
  const [docTypes, setDocTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const fileInputRef = useRef(null)
  const [uploadTarget, setUploadTarget] = useState(null)

  const loadDocs = useCallback(async () => {
    setLoading(true)
    const { data: docs } = await getPlayerDocuments(player.id)
    setDocuments(docs || [])
    // Catalogo fijo: se retiro la configuracion por club (nadie la uso en dos
    // meses y su unica promesa -- marcar requeridos -- no exigia nada).
    setDocTypes(BASE_DOCUMENT_TYPES.map((t) => ({ ...t, activo: true })))
    setLoading(false)
  }, [player.id, orgId])

  useEffect(() => { loadDocs() }, [loadDocs])

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !uploadTarget) return

    const { error } = await uploadPlayerDocument(player.id, {
      scope: uploadTarget.scope,
      docType: uploadTarget.docType,
      file,
      orgId: uploadTarget.scope === 'club' ? orgId : null,
    })

    setUploadTarget(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (error) { await alert('Error', error.message || 'No se pudo subir el documento'); return }
    loadDocs()
  }

  const triggerUpload = (scope, docType) => {
    setUploadTarget({ scope, docType })
    setTimeout(() => fileInputRef.current?.click(), 0)
  }

  const handleDelete = async (doc) => {
    if (!await confirm('Eliminar documento', 'Eliminar este documento?', { destructive: true })) return
    const { error } = await deletePlayerDocument(doc.id, doc.file_path)
    if (error) { await alert('Error', 'No se pudo eliminar el documento'); return }
    loadDocs()
  }

  const handleVerify = async (doc) => {
    const { error } = await verifyPlayerDocument(doc.id, !doc.verified)
    if (error) { await alert('Error', 'No se pudo actualizar la verificacion'); return }
    loadDocs()
  }

  const handleView = async (doc) => {
    const { url, error } = await getDocumentUrl(doc.file_path)
    if (error || !url) { await alert('Error', 'No se pudo obtener el documento'); return }
    window.open(url, '_blank')
  }

  const getDocForType = (docType) =>
    documents.find((d) => d.doc_type === docType && (d.scope !== 'club' || d.org_id === orgId))

  // Tipos activos + cualquier documento ya subido cuyo tipo no esté en la lista activa
  // (p. ej. documentos históricos de tipos retirados: se muestran para poder eliminarlos).
  const activeTypes = docTypes.filter((t) => t.activo)
  const shownValues = new Set(activeTypes.map((t) => t.value))
  const extraDocs = documents
    .filter((d) => (d.scope !== 'club' || d.org_id === orgId) && !shownValues.has(d.doc_type))

  const renderRow = (key, label, doc, docType) => (
    <div key={key} className="flex items-center justify-between py-2 px-3 rounded-md border bg-background">
      <div className="flex items-center gap-2 min-w-0">
        {doc ? (
          doc.verified
            ? <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
            : <FileCheck className="h-4 w-4 text-amber-500 shrink-0" />
        ) : (
          <FileX className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
        <span className="text-sm truncate">{label}</span>
        {doc && (
          <span className="text-xs text-muted-foreground hidden sm:inline">
            ({doc.file_name || 'archivo'})
          </span>
        )}
      </div>

      <div className="flex items-center gap-1">
        {doc ? (
          <>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleView(doc)} title="Ver">
              <Eye className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleVerify(doc)} title={doc.verified ? 'Desverificar' : 'Verificar'}>
              {doc.verified ? <XCircle className="h-3.5 w-3.5 text-amber-500" /> : <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />}
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => handleDelete(doc)} title="Eliminar">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </>
        ) : (
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => triggerUpload('club', docType)}>
            <Upload className="h-3 w-3 mr-1" /> Subir
          </Button>
        )}
      </div>
    </div>
  )

  const renderSection = () => (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Documentos</h4>
      <div className="space-y-1">
        {activeTypes.map(({ value, label }) =>
          renderRow(value, label, getDocForType(value), value)
        )}
        {extraDocs.map((doc) => renderRow(`extra-${doc.id}`, doc.doc_type, doc, doc.doc_type, false))}
        {activeTypes.length === 0 && extraDocs.length === 0 && (
          <p className="text-sm text-muted-foreground py-2">
            Este club no tiene documentos configurados. Actívalos en Configuración de la organización.
          </p>
        )}
      </div>
    </div>
  )

  if (loading) {
    return <p className="text-muted-foreground text-sm py-4">Cargando documentos...</p>
  }

  return (
    <div className="space-y-6">
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        onChange={handleUpload}
      />
      {renderSection()}
      <DocConfirmDialog />
    </div>
  )
}

// =====================================================
// TAB: Uniformes
// =====================================================
const ENTREGA_BADGE = {
  no_entregado: { label: 'No entregado', cls: 'bg-muted text-muted-foreground' },
  pendiente: { label: 'Pendiente', cls: 'bg-amber-100 text-amber-800' },
  entregado: { label: 'Entregado', cls: 'bg-green-100 text-green-800' },
}

const UniformesTab = ({ player }) => {
  const { label: deporteLabel } = useDeportes()
  const [prendas, setPrendas] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    const load = async () => {
      setLoading(true)
      const { data } = await getPrendasDeJugador(player.id)
      if (active) { setPrendas(data || []); setLoading(false) }
    }
    load()
    return () => { active = false }
  }, [player.id])

  if (loading) {
    return <p className="text-muted-foreground text-sm py-4">Cargando uniformes...</p>
  }

  if (prendas.length === 0) {
    return (
      <p className="text-muted-foreground text-sm py-4">
        Este jugador no tiene prendas asignadas. Captura tallas y entregas desde Uniformes → Tallas y entrega.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {prendas.map((p) => {
        const eb = ENTREGA_BADGE[p.estatus] || ENTREGA_BADGE.no_entregado
        return (
          <Card key={p.id}>
            <CardContent className="py-3">
              <div className="flex flex-wrap items-center gap-2">
                <Shirt className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="font-medium">{p.prenda_nombre}</span>
                {p.cantidad > 1 && <span className="text-xs text-muted-foreground">×{p.cantidad}</span>}
                {p.deporte && <Badge variant="outline" className="text-[10px]">{deporteLabel(p.deporte)}</Badge>}
                <Badge className={p.origen === 'plan' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                  {p.origen === 'plan' ? 'Plan' : 'Extra'}
                </Badge>
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-sm">
                    Talla: <strong>{p.talla || '—'}</strong>
                  </span>
                  <Badge variant={estadoDePago(p).variant}>{estadoDePago(p).label}</Badge>
                  <Badge className={eb.cls}>{eb.label}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}

// =====================================================
// MODAL PRINCIPAL
// =====================================================
const PlayerProfilePage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [player, setPlayer] = useState(null)
  const [loading, setLoading] = useState(true)

  // La query de la lista viaja en el state de la navegacion para que "Volver"
  // devuelva los filtros puestos. Una ficha abierta por un enlace pegado no trae
  // state: ahi el "Volver" cae a la lista limpia, que es lo correcto.
  const listaQuery = location.state?.listaQuery || ''
  const volverALista = () => navigate('/admin/jugadores' + listaQuery)

  const [fotoAbierta, setFotoAbierta] = useState(false)

  const loadPlayer = useCallback(async () => {
    if (!id) return
    setLoading(true)
    const { data } = await getPlayerById(id)
    setPlayer(data)
    setLoading(false)
  }, [id])

  useEffect(() => { loadPlayer() }, [loadPlayer])

  // Cambiar o quitar la foto recarga la ficha para que se vea al instante.
  const foto = usePhotoUpload({ onDone: loadPlayer })

  const statusKey = (player?.estatus || '').toLowerCase()

  return (
    <FeaturedLayout title={player?.nombre_completo || 'Jugador'} refreshAction={loadPlayer}>
      <div className="mb-4">
        <Button variant="ghost" size="sm" onClick={volverALista}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Jugadores
        </Button>
      </div>
      <div>
        {loading || !player ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Dos acciones distintas, dos botones: tocar la foto la abre a tamano
                      completo, la camara la cambia. Antes era un solo boton que decia
                      "Cambiar foto" y tenia el onClick vacio. */}
                  <div className="relative inline-flex shrink-0">
                    {player.imagen_url ? (
                      <button
                        type="button"
                        onClick={() => setFotoAbierta(true)}
                        title="Ver foto"
                        aria-label="Ver foto"
                        className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <PlayerAvatar
                          imageUrl={player.imagen_url}
                          name={player.nombre_completo}
                          size="2xl"
                        />
                      </button>
                    ) : (
                      <PlayerAvatar name={player.nombre_completo} size="2xl" />
                    )}
                    <button
                      type="button"
                      onClick={() => foto.abrir(player)}
                      title="Cambiar foto"
                      aria-label="Cambiar foto"
                      className="absolute -bottom-1 -right-1 rounded-full bg-black p-1.5 text-white shadow-lg"
                    >
                      <Camera size={14} />
                    </button>
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold truncate">{player.nombre_completo}</h2>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {player.numero && (
                        <Badge variant="outline">#{player.numero}</Badge>
                      )}
                      {player.nickname && (
                        <span className="text-sm text-muted-foreground italic">"{player.nickname}"</span>
                      )}
                      <Badge className={`${statusColors[statusKey] || 'bg-slate-100 text-slate-800'} text-xs capitalize`}>
                        {player.estatus || 'Sin estatus'}
                      </Badge>
                    </div>
                  </div>
                  <div className="ml-auto">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/admin/jugadores/${id}/editar`, { state: { listaQuery } })}
                    >
                      Editar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Tabs defaultValue="info" className="mt-4">
              <TabsList className="w-full justify-start">
                <TabsTrigger value="info">Información</TabsTrigger>
                <TabsTrigger value="grupos">
                  Grupos ({(player.jugadores_grupos || []).filter((g) => g.activo && !g.deleted).length})
                </TabsTrigger>
                <TabsTrigger value="uniformes">Uniformes</TabsTrigger>
                <TabsTrigger value="documentos">Documentos</TabsTrigger>
              </TabsList>

              <TabsContent value="info">
                <InfoTab player={player} />
              </TabsContent>

              <TabsContent value="grupos">
                <GruposTab player={player} onRefresh={loadPlayer} />
              </TabsContent>

              <TabsContent value="uniformes">
                <UniformesTab player={player} />
              </TabsContent>

              <TabsContent value="documentos">
                <DocumentosTab player={player} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>

      {foto.PhotoDialog}

      <Dialog open={fotoAbierta} onOpenChange={setFotoAbierta}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{player?.nombre_completo || 'Foto'}</DialogTitle>
          </DialogHeader>
          {player?.imagen_url && (
            <img
              src={player.imagen_url}
              alt={player.nombre_completo}
              className="mx-auto max-h-[70vh] w-auto rounded-lg object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </FeaturedLayout>
  )
}

export default PlayerProfilePage
